/**
 * 错误码枚举 - 覆盖所有可能的错误场景
 */
export enum ErrorCode {
  // 网络层错误
  NETWORK_ERROR = 'NETWORK_ERROR',           // 网络不可达
  TIMEOUT = 'TIMEOUT',                       // 请求超时
  
  // HTTP 错误（客户端问题）
  BAD_REQUEST = 'BAD_REQUEST',               // 400
  UNAUTHORIZED = 'UNAUTHORIZED',             // 401
  FORBIDDEN = 'FORBIDDEN',                   // 403
  NOT_FOUND = 'NOT_FOUND',                   // 404
  CONFLICT = 'CONFLICT',                     // 409 (唯一约束冲突)
  VALIDATION_ERROR = 'VALIDATION_ERROR',     // 422 (校验失败)
  
  // HTTP 错误（服务端问题）
  SERVER_ERROR = 'SERVER_ERROR',             // 5xx
  
  // 业务错误 (HTTP 2xx 但 success: false)
  BUSINESS_ERROR = 'BUSINESS_ERROR',
  
  // 未知错误
  UNKNOWN = 'UNKNOWN',
}

/**
 * 错误码对应的默认消息
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.NETWORK_ERROR]: '网络连接失败，请检查网络后重试',
  [ErrorCode.TIMEOUT]: '请求超时，请稍后重试',
  [ErrorCode.BAD_REQUEST]: '请求参数错误',
  [ErrorCode.UNAUTHORIZED]: '登录已过期，请重新登录',
  [ErrorCode.FORBIDDEN]: '没有权限执行此操作',
  [ErrorCode.NOT_FOUND]: '请求的资源不存在',
  [ErrorCode.CONFLICT]: '数据冲突，请刷新后重试',
  [ErrorCode.VALIDATION_ERROR]: '数据校验失败',
  [ErrorCode.SERVER_ERROR]: '服务器错误，请稍后重试',
  [ErrorCode.BUSINESS_ERROR]: '操作失败',
  [ErrorCode.UNKNOWN]: '未知错误',
}

/**
 * 校验错误详情
 */
export interface ValidationError {
  field: string
  message: string
}

/**
 * 统一应用错误类
 */
export class AppError extends Error {
  /** 错误码 */
  code: ErrorCode
  /** HTTP 状态码 */
  status?: number
  /** 校验错误详情（仅 VALIDATION_ERROR 时有值）*/
  validationErrors?: ValidationError[]
  /** 原始错误 */
  originalError?: unknown

  constructor(
    code: ErrorCode,
    message?: string,
    options?: {
      status?: number
      validationErrors?: ValidationError[]
      originalError?: unknown
    }
  ) {
    super(message || ERROR_MESSAGES[code])
    this.name = 'AppError'
    this.code = code
    this.status = options?.status
    this.validationErrors = options?.validationErrors
    this.originalError = options?.originalError
  }

  /** 是否为网络相关错误（可重试）*/
  get isRetryable(): boolean {
    return [ErrorCode.NETWORK_ERROR, ErrorCode.TIMEOUT, ErrorCode.SERVER_ERROR].includes(this.code)
  }

  /** 是否为认证错误 */
  get isAuthError(): boolean {
    return [ErrorCode.UNAUTHORIZED, ErrorCode.FORBIDDEN].includes(this.code)
  }

  /** 是否为校验错误 */
  get isValidationError(): boolean {
    return this.code === ErrorCode.VALIDATION_ERROR
  }
}

/**
 * 类型守卫：判断是否为 AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError
}
